/**
 * @voltxampere/web — `<vxa-inspector>` (Sprint 14, ADR-0022)
 *
 * Right-pane inspector with a four-mode state machine driven by
 * `selectedNodes` + `currentCircuit`:
 *
 *   - empty            → "no selection" placeholder
 *   - single-component → formula + parameter form + Run DC
 *   - multi-component  → intersection of common parameter fields
 *   - net-or-wire      → net name editor (gnd disabled)
 *
 * Edit dispatch goes through a module-level dispatcher table the
 * shell wires up in `main.ts` (mirrors the Sprint 13 canvas
 * catalog pattern). The inspector itself never imports the
 * store directly — keeps tests trivial.
 *
 * Param edits debounce at 300 ms so a typed value like "5kΩ"
 * lands as one EditParamCommand, not five.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import {
  getCommonParamFields,
  getFormula,
  getParamFields,
  renderFormula,
  type ParamField,
} from "@voltxampere/ui/inspector";
// Sprint 23 D3 — OpAmp model presets (Custom/µA741/LM358/TL072).
import { matchOpAmpPreset, OPAMP_PRESETS } from "../inspector/opamp-presets.js";
import type { ComponentInstance, Net } from "@voltxampere/schema";

import { currentCircuit } from "../store/circuit.js";
import { selectedNodes } from "../store/canvas.js";
import { currentDCResult, dcError, dcRunning } from "../store/dc-result.js";
import { channels } from "../store/channels.js";
import {
  inspectorOpenSections,
  isInspectorSectionOpen,
  setInspectorSectionOpen,
  type InspectorSectionId,
} from "../store/inspector-prefs.js";
import {
  computeComponentMeasurements,
  type ComponentMeasurements,
} from "../store/component-measurements.js";
// Sprint 34-4 — thermal frame producer for the "Sıcaklık" inspector
// section. Engine-bağımsız @voltxampere/thermal paketi computesa
// ThermalFrame; apps/web tarafı per-component (V, I) derive eder.
import { currentThermalFrame } from "../store/thermal-state.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import { t } from "../i18n/index.js";
// Sprint 80.Z B3 — migrate inspector's locale-unaware
// formatMeasurement helper to the shared locale-aware
// formatEngineering (packages/shared/src/i18n/format-engineering.ts).
// Closes Sprint 24 backlog item.
import { formatEngineering } from "@voltxampere/shared";
import { currentLocale } from "../store/locale.js";

export type InspectorMode =
  | "empty"
  | "single-component"
  | "multi-component"
  | "mixed"
  | "net-or-wire";

export interface InspectorHandlers {
  /** Apply a parameter edit (one or many components). */
  editParam(componentIds: readonly string[], fieldKey: string, newValue: unknown): void;
  /** Rename a net's identifier (excluded for ground nets). */
  editNetLabel(netId: string, oldName: string, newName: string): void;
  /** Delete a single annotation by id. */
  deleteAnnotation(id: string): void;
  /** Trigger a DC analysis run with the live circuit. */
  runDc(): void;
  /** Sprint 18 Paket C C3 — rotate the current selection 90° CW. */
  rotateSelection(): void;
  /** Sprint 18 Paket C C3 — copy the current selection (clipboard). */
  copySelection(): void;
  /** Sprint 18 Paket C C3 — delete the current selection. */
  deleteSelection(): void;
}

let inspectorHandlers: InspectorHandlers | null = null;

export function setInspectorHandlers(h: InspectorHandlers | null): void {
  inspectorHandlers = h;
}

const PARAM_DEBOUNCE_MS = 300;
const NET_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const NET_NAME_MAX_LEN = 32;

@customElement("vxa-inspector")
export class VxaInspector extends LitElement {
  private readonly _circuit = new SignalController(this, currentCircuit);
  private readonly _selected = new SignalController(this, selectedNodes);
  private readonly _dcResult = new SignalController(this, currentDCResult);
  private readonly _dcRunning = new SignalController(this, dcRunning);
  private readonly _dcError = new SignalController(this, dcError);
  // Sprint 80.Z B3 — re-render measurement tables on locale flip so
  // the new formatEngineering call picks up the Turkish/English
  // decimal-separator convention (Sprint 24 backlog closure).
  private readonly _locale = new SignalController(this, currentLocale);
  // Sprint 26 Lane 10 — collapsible-section open-set signal so a
  // toggle anywhere in the app re-renders the inspector with the
  // right `?open` state.
  private readonly _openSections = new SignalController(this, inspectorOpenSections);
  // Sprint 18 D4 — re-render the measurements table whenever any
  // probe channel pushes a sample.
  private readonly _channels = new SignalController(this, channels);
  // Sprint 34-4 — re-render when the thermal frame updates. Reads
  // `currentThermalFrame.value` from `renderThermal` for component-
  // scoped state lookup.
  private readonly _thermalFrame = new SignalController(this, currentThermalFrame);
  private readonly debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /**
   * Sprint 24 A0 (bug_009 fix) — OpAmp manual override pin map.
   * Per-component flag; when user explicitly picks "Custom" while
   * params currently match a preset (e.g. µA741 defaults), we set
   * `_opampManualOverride[cmp.id] = true` so subsequent re-renders
   * keep the dropdown on "Custom" instead of snapping back to the
   * matched preset. Cleared when params actually change (the user
   * goes back to manual editing or selects a different preset).
   * Without this flag, matchOpAmpPreset would silently override
   * the user's UI choice on every reactive re-render — a UX lie.
   */
  private readonly _opampManualOverride = new Map<string, boolean>();

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      width: 100%;
      padding: var(--s-4) var(--s-3);
      overflow-y: auto;
      font-family: var(--font-body);
      color: var(--ivory);
      background: var(--ink);
    }
    h2 {
      margin: 0 0 var(--s-2);
      font-family: var(--font-display);
      font-style: italic;
      font-weight: 400;
      font-size: var(--fs-h3);
      color: var(--brass);
    }

    /* Sprint 25 R5 R5-IA-3 — Inspector collapsible sections
       (F-IA-27). Native <details>/<summary> accordion: PARAMETRELER
       always expanded, FORMÜL + CANLI ÖLÇÜMLER + DC default
       collapsed so the inspector form is shorter on first paint
       (long forms like BJT/OpAmp no longer dump every group at
       once). User toggles persist within the session; preset
       changes re-render with the same defaults (component changes
       its identity -> re-render -> details state from template). */
    details.section-collapsible {
      margin-bottom: var(--s-4);
      padding-bottom: var(--s-3);
      border-bottom: 1px solid var(--ivory-wisp);
    }
    details.section-collapsible[open] {
      border-bottom: 1px solid var(--ivory-wisp);
    }
    details.section-collapsible:last-child {
      border-bottom: none;
    }
    /* Sprint 80.Y L-C-1 (ADR-0119) — explicit display: none for the
       non-summary children of a closed details element.

       Phase 1 Lane C-deep harness reported 12 "overlapping <tr> pairs"
       in the inspector pane (Measurements + Thermal section rows at
       overlapping y coordinates). Investigation:
       - getComputedStyle on a closed-details <table> returned
         display: table / visibility: visible (NOT display: none)
       - getBoundingClientRect returned non-zero y/h for the table
         and its rows, hence the probe's "overlap pair" report
       - Visual screenshot of the same default state showed CLEAN
         collapsed sections — no visible overlap

       Chrome's UA stylesheet for <details> uses content-visibility:
       hidden for closed-details children (per HTML Living Standard
       §4.11.1), which preserves layout calculation (for
       container-intrinsic-size) while suppressing paint. The probe's
       layout-based intersection test was tricked into reporting the
       hidden-but-laid-out rows as "overlapping" with adjacent
       sections' rows.

       This rule enforces strict display: none on closed details
       children. Effects:
         - Visually a no-op (closed details already hidden via UA)
         - Eliminates the layout-box false positive in our probe
         - Future-proofs against browser content-visibility
           inconsistencies
       Sprint 73 lesson lived: instrument finding turned out to be a
       probe artifact, not a product bug. The fix is defensive +
       honest. */
    details.section-collapsible:not([open]) > :not(summary) {
      display: none;
    }
    details.section-collapsible > summary {
      list-style: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--s-1) 0;
      user-select: none;
      gap: var(--s-2);
    }
    details.section-collapsible > summary::-webkit-details-marker {
      display: none;
    }
    details.section-collapsible > summary > h3 {
      margin: 0;
    }
    details.section-collapsible > summary > .chevron {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      color: var(--brass-faint);
      transition: transform 180ms ease;
    }
    details.section-collapsible[open] > summary > .chevron {
      transform: rotate(180deg);
    }
    details.section-collapsible > summary:hover > .chevron {
      color: var(--brass);
    }
    details.section-collapsible > summary:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: 2px;
    }
    .empty,
    .mixed {
      padding: var(--s-4);
      color: var(--ivory-dim);
      font-style: italic;
      text-align: center;
    }
    .empty .title,
    .mixed .title {
      font-size: var(--fs-base);
      margin-bottom: var(--s-2);
    }
    section {
      margin-bottom: var(--s-4);
      padding-bottom: var(--s-3);
      border-bottom: 1px solid var(--ivory-wisp);
    }
    section:last-of-type {
      border-bottom: none;
    }
    section h3 {
      margin: 0 0 var(--s-2);
      font-family: var(--font-body);
      font-style: italic;
      font-size: var(--fs-small);
      color: var(--ivory-dim);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .field {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: var(--s-2);
      align-items: center;
      margin-bottom: var(--s-2);
    }
    .field label {
      font-style: italic;
      color: var(--ivory-dim);
      font-size: var(--fs-small);
    }
    .field input,
    .field select {
      font-family: "JetBrains Mono", monospace;
      font-size: var(--fs-small);
      color: var(--ivory);
      background: var(--ink2);
      border: 1px solid var(--ivory-wisp);
      border-radius: 2px;
      padding: var(--s-1) var(--s-2);
      width: 100%;
    }
    .field input:focus-visible,
    .field select:focus-visible {
      outline: 2px solid var(--brass);
      outline-offset: 2px;
    }
    .field input[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .formula {
      padding: var(--s-3);
      background: var(--ink2);
      border-left: 2px solid var(--brass-faint);
      font-size: var(--fs-base);
      min-height: 2em;
    }
    button {
      font-family: var(--font-body);
      font-size: var(--fs-small);
      color: var(--brass);
      background: transparent;
      border: 1px solid var(--brass);
      padding: var(--s-2) var(--s-3);
      min-height: 44px;
      cursor: pointer;
      letter-spacing: 0.02em;
    }
    button:hover {
      background: rgba(209, 150, 72, 0.08);
    }
    button:focus-visible {
      outline: 2px solid var(--brass);
      outline-offset: 2px;
    }
    button[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .dc-status {
      font-style: italic;
      color: var(--ivory-dim);
      font-size: var(--fs-small);
      margin-top: var(--s-2);
    }
    .dc-error {
      color: var(--danger, #c53030);
    }
    .hint {
      display: block;
      margin-top: var(--s-1);
      font-style: italic;
      color: var(--ivory-dim);
      font-size: var(--fs-small);
    }

    /* Sprint 18 D6 — breadcrumb above the heading */
    .breadcrumb {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--ivory-dim);
      margin: 0 0 var(--s-2);
      display: flex;
      gap: var(--s-1);
      align-items: center;
      flex-wrap: wrap;
    }
    .breadcrumb .crumb-leaf {
      color: var(--brass);
    }
    .breadcrumb .sep {
      color: var(--ivory-faint);
    }

    /* Sprint 18 D4 — live measurements table */
    .measurements-table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--fs-small);
    }
    .measurements-table th {
      text-align: left;
      font-family: var(--font-body);
      font-style: italic;
      font-weight: 400;
      color: var(--ivory-dim);
      padding: var(--s-1) var(--s-2);
      border-bottom: 1px solid var(--ivory-wisp);
    }
    .measurements-table td.value {
      text-align: right;
      font-family: var(--font-mono);
      font-variant-numeric: tabular-nums;
      color: var(--brass);
      padding: var(--s-1) var(--s-2);
      border-bottom: 1px solid var(--ivory-wisp);
    }
    .measurements-table tr:last-child th,
    .measurements-table tr:last-child td {
      border-bottom: none;
    }
    /* Sprint 34-4 — thermal severity bar (3-stage color) */
    .thermal-severity-bar {
      position: relative;
      width: 100%;
      height: 8px;
      background: var(--ink-2);
      border: 1px solid var(--ivory-wisp);
      border-radius: 2px;
      overflow: hidden;
    }
    .thermal-severity-fill {
      height: 100%;
      transition: width 200ms ease;
    }
    .severity-ok .thermal-severity-fill {
      background: rgba(255, 180, 80, 0.85);
    }
    .severity-warn .thermal-severity-fill {
      background: rgba(255, 80, 50, 0.95);
    }
    .severity-danger .thermal-severity-fill {
      background: rgba(255, 60, 40, 1);
      box-shadow: 0 0 6px rgba(255, 60, 40, 0.7);
    }
    .measurements-empty {
      margin: var(--s-2) 0 0;
      padding: var(--s-3);
      background: var(--ink-2);
      border-left: 2px solid var(--brass-faint);
      font-style: italic;
      color: var(--ivory-dim);
      font-size: var(--fs-small);
    }

    /* Sprint 18 Paket C C3 tolerance picker + action row.
       Sprint 25 R5 R5-VIS-5 (F-V-10) — narrow inspector
       containers used to clip the rightmost chip (16/16 preset
       PNGs in UX_QUALITY_AUDIT) because the grid auto column
       overflowed. Switching to flex-wrap lets the buttons drop
       to a second line when space is tight, and min-width: 0
       prevents grid-style growth. */
    .tolerance-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--s-2);
      align-items: center;
      justify-content: space-between;
      margin-top: var(--s-2);
      min-width: 0;
    }
    .tolerance-label {
      font-style: italic;
      color: var(--ivory-dim);
      font-size: var(--fs-small);
    }
    .tolerance-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: var(--s-1);
      min-width: 0;
    }
    .tolerance-btn {
      font-family: var(--font-mono);
      font-size: var(--fs-small);
      letter-spacing: 0.04em;
      padding: var(--s-1) var(--s-2);
      background: transparent;
      color: var(--brass-faint);
      border: 1px solid var(--brass-faint);
    }
    .tolerance-btn:hover {
      background: rgba(209, 150, 72, 0.06);
    }
    .tolerance-btn.active {
      background: var(--brass);
      color: var(--ink);
      border-color: var(--brass);
    }
    .actions-section {
      margin-top: var(--s-2);
    }
    .action-row {
      display: flex;
      gap: var(--s-2);
    }
    .action-row button {
      flex: 1;
      padding: var(--s-2) var(--s-2);
      font-size: var(--fs-small);
    }
    .action-row button.destructive {
      color: var(--danger, #c53030);
      border-color: var(--danger, #c53030);
    }
    .action-row button.destructive:hover {
      background: rgba(197, 48, 48, 0.08);
    }

    /* Sprint 26 Lane 4 R5-IA-6 — keyboard shortcut hint chip.
       Mirrors the topbar pattern. The chip is decorative
       (aria-hidden) so screen readers do not announce the
       shortcut twice; the button's title= already carries it. */
    button kbd {
      display: inline-block;
      margin-inline-start: 6px;
      padding: 0 4px;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.04em;
      color: var(--ivory-dim);
      border: 1px solid var(--brass-faint);
      border-radius: 2px;
      background: transparent;
      vertical-align: baseline;
    }
  `;

  override render(): unknown {
    const mode = this.computeMode();
    // Sprint 14 D2 (Sprint 11 carry-over): every render branch
    // returns an *array* of TemplateResults. Lit's static-template
    // interpolation matching has been observed in Sprint 14 to
    // reorder `${conditional ? html : nothing}` and adjacent
    // `${this.method()}` interpolations under happy-dom +
    // vitest dev-mode (params section landed inside the DC
    // button, button text into the DC status slot, etc.).
    // Array-return sidesteps the issue by giving Lit one
    // top-level slot per fragment.
    switch (mode) {
      case "empty":
        return this.renderEmpty();
      case "single-component":
        return this.renderSingleComponent();
      case "multi-component":
        return this.renderMultiComponent();
      case "mixed":
        return this.renderMixed();
      case "net-or-wire":
        return this.renderNetOrWire();
    }
  }

  override updated(): void {
    this.bindEventDelegation();
    const formulaSlot = this.renderRoot.querySelector<HTMLElement>(".formula");
    if (!formulaSlot) return;
    const cmp = this.singleSelectedComponent();
    if (!cmp) return;
    const tpl = getFormula(cmp);
    if (tpl === null) {
      formulaSlot.textContent = t("inspector.formula.missing");
      return;
    }
    renderFormula(tpl, formulaSlot, {
      onError: (err) => {
        console.warn("[vxa-inspector] KaTeX render error", err);
      },
    });
  }

  /**
   * Lit's `@event=${...}` attribute syntax behaves erratically
   * inside happy-dom + vitest dev-mode for this element (the
   * interpolation falls into child position and the listener is
   * never registered). We bypass the issue with a single
   * delegated `click` listener on the shadow root and route by
   * `data-action` attribute. Sprint 14.0.x can revisit when the
   * Lit / happy-dom interplay is understood.
   */
  /**
   * Sprint 26 Lane 10 — `<details>` `toggle` event handler.
   * Bound as an arrow-function field so it survives template
   * re-renders without re-wiring per Lit-element-instance. The
   * section id is read from `data-section-id` so adding a new
   * collapsible section needs no handler change.
   */
  private readonly handleSectionToggle = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const id = target.dataset.sectionId ?? "";
    const open = (target as HTMLDetailsElement).open;
    // Defensive: only persist for ids the store knows about. If a
    // future component drops a section before the store list is
    // updated, a stale toggle does not corrupt persistence.
    const known: readonly InspectorSectionId[] = ["formula", "measurements", "dc"];
    if (!(known as readonly string[]).includes(id)) return;
    setInspectorSectionOpen(id as InspectorSectionId, open);
  };

  private delegationAttached = false;
  private bindEventDelegation(): void {
    if (this.delegationAttached) return;
    const root = this.renderRoot as ShadowRoot;
    root.addEventListener("click", (e: Event) => {
      const target = e.target as HTMLElement | null;
      const trigger = target?.closest<HTMLElement>("[data-action]");
      const action = trigger?.dataset.action;
      if (action === "run-dc") this.handleRunDc();
      if (action === "probe-toggle" && trigger) this.handleProbeToggle(trigger);
      // Sprint 18 Paket C C3 — tolerance + action buttons.
      if (action === "set-tolerance" && trigger) this.handleSetTolerance(trigger);
      if (action === "rotate-selection") inspectorHandlers?.rotateSelection();
      if (action === "copy-selection") inspectorHandlers?.copySelection();
      if (action === "delete-selection") inspectorHandlers?.deleteSelection();
    });
    root.addEventListener("input", (e: Event) => {
      this.handleDelegatedInput(e);
    });
    root.addEventListener("change", (e: Event) => {
      this.handleDelegatedInput(e);
    });
    this.delegationAttached = true;
  }

  private handleDelegatedInput(e: Event): void {
    const target = e.target as HTMLInputElement | HTMLSelectElement | null;
    if (!target) return;
    const kind = target.dataset.input;
    if (kind === "param") {
      const cmpIds = (target.dataset.cmpids ?? "").split(",").filter(Boolean);
      const fieldKey = target.dataset.fieldKey ?? "";
      const fieldType = target.dataset.fieldType ?? "string";
      if (cmpIds.length === 0 || !fieldKey) return;
      let value: unknown;
      if (fieldType === "boolean") {
        value = (target as HTMLInputElement).checked;
      } else if (fieldType === "number" || fieldType === "range") {
        // Sprint 23 B4 — range input value is a string per HTMLInputElement
        // contract; parse with the same numeric guard as `number`.
        const parsed = parseFloat(target.value);
        if (Number.isNaN(parsed)) return;
        value = parsed;
      } else {
        value = target.value;
      }
      this.scheduleParamEdit(cmpIds, fieldKey, value);
      return;
    }
    if (kind === "net-name") {
      const netId = target.dataset.netId ?? "";
      const oldName = target.dataset.oldName ?? "";
      const next = target.value;
      if (!netId || target.disabled) return;
      if (next === "" || next === oldName) return;
      if (!NET_NAME_PATTERN.test(next)) return;
      const debounceKey = `net#${netId}`;
      const existing = this.debounceTimers.get(debounceKey);
      if (existing !== undefined) clearTimeout(existing);
      const timer = setTimeout(() => {
        this.debounceTimers.delete(debounceKey);
        inspectorHandlers?.editNetLabel(netId, oldName, next);
      }, PARAM_DEBOUNCE_MS);
      this.debounceTimers.set(debounceKey, timer);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const timer of this.debounceTimers.values()) clearTimeout(timer);
    this.debounceTimers.clear();
  }

  // ── public test surface ──────────────────────────────────────────

  get currentMode(): InspectorMode {
    return this.computeMode();
  }

  // ── mode dispatch ────────────────────────────────────────────────

  private computeMode(): InspectorMode {
    const ids = this._selected.value;
    const circuit = this._circuit.value;
    if (!circuit || ids.length === 0) return "empty";
    const components = circuit.components.filter((c) => ids.includes(c.id));
    const wires = circuit.wires.filter((w) => ids.includes(w.id));
    if (components.length === 0 && wires.length >= 1) return "net-or-wire";
    if (components.length === 1 && wires.length === 0 && ids.length === 1) {
      return "single-component";
    }
    if (components.length >= 2 && wires.length === 0) {
      const types = new Set(components.map((c) => c.type));
      if (types.size === 1) return "multi-component";
      const common = getCommonParamFields([...types]);
      return common.length > 0 ? "multi-component" : "mixed";
    }
    return "mixed";
  }

  private singleSelectedComponent(): ComponentInstance | null {
    const ids = this._selected.value;
    if (ids.length !== 1) return null;
    const circuit = this._circuit.value;
    if (!circuit) return null;
    const id = ids[0];
    if (id === undefined) return null;
    return circuit.components.find((c) => c.id === id) ?? null;
  }

  private selectedComponents(): readonly ComponentInstance[] {
    const ids = this._selected.value;
    const circuit = this._circuit.value;
    if (!circuit) return [];
    return circuit.components.filter((c) => ids.includes(c.id));
  }

  private firstSelectedNet(): Net | null {
    const ids = this._selected.value;
    const circuit = this._circuit.value;
    if (!circuit) return null;
    for (const id of ids) {
      const wire = circuit.wires.find((w) => w.id === id);
      if (!wire) continue;
      const net = circuit.nets.find((n) => n.id === wire.netId);
      if (net) return net;
    }
    return null;
  }

  // ── render branches ──────────────────────────────────────────────

  private renderEmpty(): TemplateResult {
    return html`
      <div class="empty">
        <p class="title">${t("inspector.empty.title")}</p>
        <p>${t("inspector.empty.subtitle")}</p>
      </div>
    `;
  }

  private renderMixed(): TemplateResult {
    return html`
      <div class="mixed">
        <p class="title">${t("inspector.mixed.title")}</p>
        <p>${t("inspector.mixed.subtitle")}</p>
      </div>
    `;
  }

  private renderSingleComponent(): unknown {
    const cmp = this.singleSelectedComponent();
    if (!cmp) return this.renderEmpty();
    const fields = getParamFields(cmp.type);
    const breadcrumb = this.renderBreadcrumb([
      t("inspector.breadcrumb.component"),
      cmp.label ?? cmp.type,
      t("inspector.breadcrumb.properties"),
    ]);
    const heading = html`<h2>${cmp.label ?? cmp.type}</h2>`;
    // Sprint 25 R5 R5-IA-3 — FORMÜL collapsible (default closed; the
    // formula box is decorative until KaTeX expression is wired,
    // and stuffing it open by default pushes PARAMETRELER below
    // the fold on tall components). Sprint 26 Lane 10 — open
    // state hydrated from IDB; toggle persists 500 ms debounced.
    const formula = html`<details
      class="section-collapsible"
      data-section-id="formula"
      ?open=${isInspectorSectionOpen("formula")}
      @toggle=${this.handleSectionToggle}
    >
      <summary>
        <h3>${t("inspector.section.formula")}</h3>
        <span class="chevron" aria-hidden="true">▾</span>
      </summary>
      <div class="formula"></div>
    </details>`;
    // Sprint 23 D3 — OpAmp preset dropdown (Custom/µA741/LM358/TL072).
    // Render only for op-amp components; preset selection bulk-updates
    // openLoopGain + gainBandwidthProduct via two scheduleParamEdit calls.
    const opampPreset = cmp.type === "opamp" ? this.renderOpAmpPreset(cmp) : nothing;
    // PARAMETRELER stays as a flat <section> — always expanded
    // (the user came to the inspector to edit values).
    const params =
      fields.length === 0
        ? nothing
        : html`<section>
            <h3>${t("inspector.section.params")}</h3>
            ${opampPreset} ${fields.map((f) => this.renderField(f, cmp.params[f.key], [cmp.id]))}
            ${this.renderToleranceRow(cmp)}
          </section>`;
    // CANLI ÖLÇÜMLER — collapsible, default closed.
    const measurements = this.renderMeasurements(cmp);
    // Sprint 34-4 SICAKLIK — thermal readout collapsible section.
    const thermal = this.renderThermal(cmp);
    // DC — collapsible, default closed.
    const dc = html`<details
      class="section-collapsible"
      data-section-id="dc"
      ?open=${isInspectorSectionOpen("dc")}
      @toggle=${this.handleSectionToggle}
    >
      <summary>
        <h3>DC</h3>
        <span class="chevron" aria-hidden="true">▾</span>
      </summary>
      <button
        class="run-dc"
        type="button"
        aria-label="Run DC"
        title="Run DC (F5)"
        data-action="run-dc"
      >
        ${t("inspector.button.runDc")}<kbd aria-hidden="true">F5</kbd></button
      >${this.renderDcStatus()}
    </details>`;
    const actions = this.renderActionRow(cmp);
    return [breadcrumb, heading, formula, params, measurements, thermal, dc, actions];
  }

  /**
   * Sprint 18 Paket C C3 — tolerance picker. Resistors, capacitors
   * and inductors all carry component-tolerance metadata. The
   * three-button row mirrors the mockup exactly and writes
   * `params.tolerance` (Sprint 19+ Monte Carlo will consume it).
   *
   * Other component families (sources, semis, etc.) have no
   * tolerance concept so the row is suppressed.
   */
  private renderToleranceRow(cmp: ComponentInstance): TemplateResult | typeof nothing {
    if (!isToleranceCarrier(cmp.type)) return nothing;
    const current = readTolerance(cmp.params);
    const buttons = TOLERANCE_OPTIONS.map((opt) => {
      const active = approximatelyEqual(current, opt.value);
      return html`<button
        type="button"
        class="tolerance-btn ${active ? "active" : ""}"
        data-action="set-tolerance"
        data-cmpids=${cmp.id}
        data-tolerance=${String(opt.value)}
        aria-pressed=${active ? "true" : "false"}
        aria-label=${opt.label}
        title=${opt.label}
      >
        ${opt.label}
      </button>`;
    });
    return html`<div class="tolerance-row">
      <span class="tolerance-label">${t("inspector.param.tolerance")}</span>
      <div class="tolerance-buttons">${buttons}</div>
    </div>`;
  }

  /**
   * Sprint 18 Paket C C3 — action button row at the bottom of the
   * single-component view. Three buttons map to existing canvas
   * commands; the Delete button gets a `destructive` modifier so
   * the danger styling kicks in only when the user is about to
   * remove a component.
   */
  private renderActionRow(cmp: ComponentInstance): TemplateResult {
    void cmp;
    return html`<section class="actions-section">
      <h3>${t("inspector.section.actions")}</h3>
      <div class="action-row">
        <button
          type="button"
          data-action="rotate-selection"
          aria-label=${t("inspector.action.rotate")}
          title=${t("inspector.action.rotate") + " (Shift+R)"}
        >
          ${t("inspector.action.rotate")}<kbd aria-hidden="true">⇧R</kbd>
        </button>
        <button
          type="button"
          data-action="copy-selection"
          aria-label=${t("inspector.action.copy")}
          title=${t("inspector.action.copy") + " (Cmd+C)"}
        >
          ${t("inspector.action.copy")}<kbd aria-hidden="true">⌘C</kbd>
        </button>
        <button
          type="button"
          class="destructive"
          data-action="delete-selection"
          aria-label=${t("inspector.action.delete")}
          title=${t("inspector.action.delete") + " (Del)"}
        >
          ${t("inspector.action.delete")}<kbd aria-hidden="true">Del</kbd>
        </button>
      </div>
    </section>`;
  }

  /**
   * Sprint 18 D6 — breadcrumb fragment above the inspector
   * heading. Plain text crumbs joined by middle-dot separators.
   * The leaf is highlighted brass so the user reads "you are here".
   *
   * Sprint 19+ may make the crumbs clickable for paket B's
   * INSPECTOR pane — for now they are display-only.
   */
  private renderBreadcrumb(crumbs: readonly string[]): TemplateResult {
    const items: TemplateResult[] = [];
    crumbs.forEach((crumb, idx) => {
      const isLeaf = idx === crumbs.length - 1;
      if (idx > 0) {
        items.push(html`<span class="sep" aria-hidden="true">›</span>`);
      }
      items.push(html`<span class=${isLeaf ? "crumb-leaf" : "crumb"}>${crumb}</span>`);
    });
    return html`<nav class="breadcrumb" aria-label="Breadcrumb">${items}</nav>`;
  }

  /**
   * Sprint 18 D4 — eight-row live measurement table for the
   * selected component. Reads from the channel sample buffer
   * bound to the component's first non-ground terminal. When no
   * probe is bound we render an em-dash placeholder block with a
   * one-line hint pointing the user at probe-binding (right-click
   * a net or use the CHn buttons in net mode).
   */
  private renderMeasurements(cmp: ComponentInstance): TemplateResult {
    // Force the controller to subscribe so a new sample retriggers
    // the render. Reading `.value` inside the render function ties
    // us into the signals graph identical to `_dcResult`.
    void this._channels.value;
    const m = computeComponentMeasurements(cmp, this._circuit.value);
    // Sprint 25 R5 R5-IA-3 — measurements section is collapsible
    // (default closed). Live readings are useful but the user
    // typically lands on the inspector to EDIT, not to read; the
    // collapsed default keeps the form compact. Sprint 26 Lane 10
    // — open state hydrated from IDB; toggle persists.
    return html`<details
      class="section-collapsible measurements"
      data-section-id="measurements"
      ?open=${isInspectorSectionOpen("measurements")}
      @toggle=${this.handleSectionToggle}
    >
      <summary>
        <h3>${t("inspector.section.measurements")}</h3>
        <span class="chevron" aria-hidden="true">▾</span>
      </summary>
      ${m === null
        ? html`<p class="measurements-empty">${t("inspector.measurements.empty")}</p>`
        : this.renderMeasurementRows(m)}
    </details>`;
  }

  private renderMeasurementRows(m: ComponentMeasurements): TemplateResult {
    const rows: readonly { key: string; label: string; value: number | null; unit: string }[] = [
      { key: "vrms", label: t("inspector.measurements.vrms"), value: m.vrms, unit: "V" },
      { key: "vpeak", label: t("inspector.measurements.vpeak"), value: m.vpeak, unit: "V" },
      { key: "vpp", label: t("inspector.measurements.vpp"), value: m.vpp, unit: "V" },
      { key: "vavg", label: t("inspector.measurements.vavg"), value: m.vavg, unit: "V" },
      { key: "vmax", label: t("inspector.measurements.vmax"), value: m.vmax, unit: "V" },
      { key: "vmin", label: t("inspector.measurements.vmin"), value: m.vmin, unit: "V" },
      {
        key: "frequency",
        label: t("inspector.measurements.frequency"),
        value: m.frequency,
        unit: "Hz",
      },
      {
        key: "period",
        label: t("inspector.measurements.period"),
        value: m.period,
        unit: "s",
      },
    ];
    return html`
      <table class="measurements-table">
        <tbody>
          ${rows.map(
            (r) => html`
              <tr data-key=${r.key}>
                <th scope="row">${r.label}</th>
                <td class="value">${formatEngineering(r.value, r.unit, this._locale.value)}</td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  /**
   * Sprint 34-4 — "Sıcaklık" canlı ölçüm bölümü. Engine result'tan
   * türetilen ThermalFrame'den seçili component'in T_C / P_W /
   * severity okumalarını gösterir. Severity bar 3-stage: < 0.5 ok
   * (orange-yellow), < 1.0 warn (red), ≥ 1.0 danger (bright red).
   *
   * Boş frame veya unknown component → "termal veri yok" placeholder.
   */
  private renderThermal(cmp: ComponentInstance): TemplateResult {
    // Subscribe to the signal — re-render on every frame update.
    const frame = this._thermalFrame.value;
    const state = frame?.components.get(cmp.id) ?? null;
    return html`<details
      class="section-collapsible thermal"
      data-section-id="thermal"
      ?open=${isInspectorSectionOpen("thermal")}
      @toggle=${this.handleSectionToggle}
    >
      <summary>
        <h3>${t("inspector.thermal.title")}</h3>
        <span class="chevron" aria-hidden="true">▾</span>
      </summary>
      ${state === null
        ? html`<p class="measurements-empty">${t("inspector.thermal.noData")}</p>`
        : this.renderThermalRows(state)}
    </details>`;
  }

  private renderThermalRows(state: {
    readonly T_C: number;
    readonly P_W: number;
    readonly severity: number;
    readonly T_max_C: number;
  }): TemplateResult {
    const severityClass = state.severity < 0.5 ? "ok" : state.severity < 1.0 ? "warn" : "danger";
    const fillPct = Math.min(state.severity, 1) * 100;
    return html`
      <table class="measurements-table thermal-table">
        <tbody>
          <tr data-key="temperature">
            <th scope="row">${t("inspector.thermal.temperature")}</th>
            <td class="value">${state.T_C.toFixed(1)} °C</td>
          </tr>
          <tr data-key="t-max">
            <th scope="row">${t("inspector.thermal.tMax")}</th>
            <td class="value">${state.T_max_C.toFixed(0)} °C</td>
          </tr>
          <tr data-key="power">
            <th scope="row">${t("inspector.thermal.power")}</th>
            <td class="value">${formatEngineering(state.P_W, "W", this._locale.value)}</td>
          </tr>
          <tr data-key="severity">
            <th scope="row">${t("inspector.thermal.severity")}</th>
            <td class="value">
              <div
                class="thermal-severity-bar severity-${severityClass}"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow=${String(Math.round(fillPct))}
                data-testid="thermal-severity-bar"
              >
                <div class="thermal-severity-fill" style="width: ${String(fillPct)}%"></div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  private renderMultiComponent(): unknown {
    const cmps = this.selectedComponents();
    const types = [...new Set(cmps.map((c) => c.type))];
    const common = getCommonParamFields(types);
    if (common.length === 0) return this.renderMixed();
    const ids = cmps.map((c) => c.id);
    const breadcrumb = this.renderBreadcrumb([
      t("inspector.breadcrumb.selection"),
      t("inspector.multi.heading", { count: String(cmps.length) }),
    ]);
    const heading = html`<h2>${t("inspector.multi.heading", { count: String(cmps.length) })}</h2>`;
    const params = html`<section>
      <h3>${t("inspector.section.params")}</h3>
      ${common.map((f) => this.renderField(f, undefined, ids))}
    </section>`;
    // Sprint 14 D2 — array return defeats happy-dom Lit reorder bug.
    return [breadcrumb, heading, params];
  }

  private renderNetOrWire(): unknown {
    const net = this.firstSelectedNet();
    if (!net) return this.renderEmpty();
    const probeButtons = this.renderProbeButtons(net.id);
    const breadcrumb = this.renderBreadcrumb([t("inspector.breadcrumb.net"), net.name]);
    const body = html`<section>
      <h2>${t("inspector.section.net")}</h2>
      <div class="field">
        <label for="net-name">${t("inspector.label.netName")}</label>
        <input
          id="net-name"
          type="text"
          data-input="net-name"
          data-net-id=${net.id}
          data-old-name=${net.name}
          value=${net.name}
          ?disabled=${net.isGround}
          maxlength=${String(NET_NAME_MAX_LEN)}
        />
      </div>
      ${net.isGround ? html`<small class="hint">${t("inspector.hint.gnd")}</small>` : null}
      ${probeButtons}
    </section>`;
    // Sprint 14 D2 — array return defeats happy-dom Lit reorder bug.
    return [breadcrumb, body];
  }

  /** Sprint 15 — handler for "Probe to CHn" buttons in net mode.
   *  Resolved at click time (not via the dispatcher table) because
   *  the channel store is in `apps/web/src/store/`, not the
   *  inspector's `InspectorHandlers` contract. */
  private handleProbeToggle(trigger: HTMLElement): void {
    const channel = trigger.dataset.channel;
    const netId = trigger.dataset.netid;
    if (!channel || !netId) return;
    void Promise.all([import("../store/channels.js"), import("../store/probes.js")]).then(
      ([{ bindProbe, unbindProbe, channels }, { probeBindings }]) => {
        const channelId = channel as "ch1" | "ch2" | "ch3" | "ch4";
        const existingBinding = probeBindings.value.get(netId);
        if (existingBinding === channelId) {
          unbindProbe(channelId);
        } else {
          // Unbind any prior binding on this channel first so each
          // CH binds to at most one net.
          if (channels.value.get(channelId)?.probeBinding) {
            unbindProbe(channelId);
          }
          bindProbe(channelId, netId);
        }
        this.requestUpdate();
      },
    );
  }

  private renderProbeButtons(netId: string): TemplateResult {
    // Sprint 15: net-mode "Probe to CHn" buttons. The active
    // binding (if any) wins highlight; clicking again unbinds.
    // We import the probe stores lazily so the inspector module
    // does not pull the channel store into pre-Sprint-15
    // consumers (the placeholder DC-only inspector).
    const items: TemplateResult[] = [];
    for (const ch of ["ch1", "ch2", "ch3", "ch4"] as const) {
      items.push(
        html`<button
          type="button"
          data-action="probe-toggle"
          data-channel=${ch}
          data-netid=${netId}
          title=${`Probe ${ch.toUpperCase()} ↔ ${netId.slice(0, 6)}`}
        >
          ${ch.toUpperCase()}
        </button>`,
      );
    }
    return html`<div class="probe-buttons" aria-label="Probe binding">
      <small class="hint">Probe</small>
      ${items}
    </div>`;
  }

  /**
   * Sprint 23 D3 — OpAmp preset dropdown row. Uses native `<select>`
   * (renderField "enum" tipiyle benzer ama multi-param onChange handler
   * gerekiyor — preset seçimi gain + GBW birlikte günceller).
   */
  private renderOpAmpPreset(cmp: ComponentInstance): TemplateResult {
    // Sprint 24 A0 (bug_009 fix) — when user has explicitly picked
    // "Custom" we honor that choice over `matchOpAmpPreset`, which
    // otherwise would snap the dropdown back to the preset whose
    // values currently match params. Manual override flag clears
    // the moment params actually change (a different preset
    // selection or hand-typed param edit clears the flag below).
    const userPickedCustom = this._opampManualOverride.get(cmp.id) === true;
    const matched = matchOpAmpPreset(cmp.params);
    const currentId = userPickedCustom ? "custom" : matched;
    const handle = (e: Event): void => {
      const target = e.target as HTMLSelectElement | null;
      if (!target) return;
      const newId = target.value;
      const preset = OPAMP_PRESETS.find((p) => p.id === newId);
      if (!preset) return;
      if (preset.id === "custom") {
        // Pin the dropdown on Custom until the user changes
        // anything else. No params write — engine state stays
        // exactly as-is.
        this._opampManualOverride.set(cmp.id, true);
        this.requestUpdate();
        return;
      }
      // Selecting a non-Custom preset clears the manual override
      // flag and writes the preset's canonical params.
      this._opampManualOverride.delete(cmp.id);
      // Multi-param update: schedule two edit-param commands. Inspector
      // debounce coalesces them into the auto-save lifecycle.
      if (preset.openLoopGain !== undefined) {
        this.scheduleParamEdit([cmp.id], "openLoopGain", preset.openLoopGain);
      }
      if (preset.gainBandwidthProduct !== undefined) {
        this.scheduleParamEdit([cmp.id], "gainBandwidthProduct", preset.gainBandwidthProduct);
      }
    };
    return html`
      <div class="field" data-testid="opamp-preset-row">
        <label for="opamp-preset-${cmp.id}">${t("inspector.opamp.preset.label")}</label>
        <select id="opamp-preset-${cmp.id}" data-testid="opamp-preset-select" @change=${handle}>
          ${OPAMP_PRESETS.map(
            (p) =>
              html`<option value=${p.id} ?selected=${p.id === currentId}>
                ${t(p.labelKey as never)}
              </option>`,
          )}
        </select>
      </div>
    `;
  }

  private renderField(
    field: ParamField,
    currentValue: unknown,
    componentIds: readonly string[],
  ): TemplateResult {
    const key = `${componentIds.join(",")}#${field.key}`;
    const cmpData = componentIds.join(",");
    if (field.type === "boolean") {
      return html`
        <div class="field">
          <label for=${key}>${t(field.label as never)}</label>
          <input
            id=${key}
            type="checkbox"
            data-input="param"
            data-cmpids=${cmpData}
            data-field-key=${field.key}
            data-field-type="boolean"
            .checked=${Boolean(currentValue)}
          />
        </div>
      `;
    }
    if (field.type === "range") {
      // Sprint 23 B4 — Potentiometer slider (and any other 0..1
      // ranged param). Renders a horizontal range input + live value
      // readout. Reuses `data-input="param"` delegation; the input
      // event fires continuously while the user drags so auto-save
      // debounce smooths the param-edit traffic.
      const numericValue =
        typeof currentValue === "number" && Number.isFinite(currentValue) ? currentValue : 0;
      const min = field.min ?? 0;
      const max = field.max ?? 1;
      const step = field.step ?? 0.01;
      // Clamp the displayed value to the slider range so HTML range
      // input does not silently snap the source-of-truth.
      const clamped = Math.max(min, Math.min(max, numericValue));
      return html`
        <div class="field field-range">
          <label for=${key}>${t(field.label as never)}</label>
          <div class="range-row">
            <input
              id=${key}
              type="range"
              data-input="param"
              data-cmpids=${cmpData}
              data-field-key=${field.key}
              data-field-type="range"
              min=${String(min)}
              max=${String(max)}
              step=${String(step)}
              .value=${String(clamped)}
            />
            <output class="range-value">${clamped.toFixed(3)}</output>
          </div>
        </div>
      `;
    }
    if (field.type === "enum" && field.options) {
      return html`
        <div class="field">
          <label for=${key}>${t(field.label as never)}</label>
          <select
            id=${key}
            data-input="param"
            data-cmpids=${cmpData}
            data-field-key=${field.key}
            data-field-type="enum"
          >
            ${field.options.map(
              (o) => html`
                <option value=${o.value} ?selected=${currentValue === o.value}>${o.label}</option>
              `,
            )}
          </select>
        </div>
      `;
    }
    const valueStr = formatParamValueForInput(currentValue);
    return html`
      <div class="field">
        <label for=${key}
          >${t(field.label as never)}${field.unit
            ? html` <small>(${field.unit})</small>`
            : null}</label
        >
        <input
          id=${key}
          type=${field.type === "number" ? "number" : "text"}
          data-input="param"
          data-cmpids=${cmpData}
          data-field-key=${field.key}
          data-field-type=${field.type}
          value=${valueStr}
          placeholder=${field.placeholder ?? ""}
          aria-label=${t(field.label as never)}
        />
      </div>
    `;
  }

  private renderDcStatus(): TemplateResult {
    if (this._dcRunning.value) {
      return html`<p class="dc-status">${t("inspector.dc.running")}</p>`;
    }
    if (this._dcError.value) {
      return html`<p class="dc-status dc-error">
        ${t("inspector.dc.error", { message: this._dcError.value })}
      </p>`;
    }
    if (!this._dcResult.value) {
      return html`<p class="dc-status">${t("inspector.dc.idle")}</p>`;
    }
    const r = this._dcResult.value;
    return html`<p class="dc-status">${r.converged ? "✓" : "✗"} ${String(r.iterations)} iter</p>`;
  }

  // ── handlers ─────────────────────────────────────────────────────

  private scheduleParamEdit(
    componentIds: readonly string[],
    fieldKey: string,
    newValue: unknown,
  ): void {
    const key = `${componentIds.join(",")}#${fieldKey}`;
    const existing = this.debounceTimers.get(key);
    if (existing !== undefined) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.debounceTimers.delete(key);
      inspectorHandlers?.editParam(componentIds, fieldKey, newValue);
    }, PARAM_DEBOUNCE_MS);
    this.debounceTimers.set(key, timer);
  }

  private readonly handleRunDc = (): void => {
    inspectorHandlers?.runDc();
  };

  /** Sprint 18 Paket C C3 — write `params.tolerance` for every
   *  selected component. Tolerance is metadata only this sprint;
   *  Sprint 19+ Monte Carlo will consume it. */
  private handleSetTolerance(trigger: HTMLElement): void {
    const cmpIds = (trigger.dataset.cmpids ?? "").split(",").filter(Boolean);
    const tolerance = parseFloat(trigger.dataset.tolerance ?? "");
    if (cmpIds.length === 0 || !Number.isFinite(tolerance)) return;
    inspectorHandlers?.editParam(cmpIds, "tolerance", tolerance);
  }
}

/**
 * Sprint 18 Paket C C3 — tolerance picker contract.
 *
 * The mockup-canonical three-button row is %1 / %5 / %10. Stored
 * value is a fraction (0.01 / 0.05 / 0.10) so a downstream Monte
 * Carlo consumer multiplies by the parameter directly.
 */
const TOLERANCE_OPTIONS: readonly { readonly value: number; readonly label: string }[] = [
  { value: 0.01, label: "%1" },
  { value: 0.05, label: "%5" },
  { value: 0.1, label: "%10" },
];

const TOLERANCE_CARRIER_TYPES: ReadonlySet<string> = new Set(["resistor", "capacitor", "inductor"]);

export function isToleranceCarrier(type: string): boolean {
  return TOLERANCE_CARRIER_TYPES.has(type);
}

/** Read the tolerance metadata off a component's params bag. The
 *  field is optional — components authored before Sprint 18 omit
 *  it entirely; when absent we treat the default as 5 % so the
 *  picker highlights the standard SPICE value. */
export function readTolerance(params: Record<string, unknown>): number {
  const raw = params.tolerance;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return raw;
  }
  return 0.05;
}

function approximatelyEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

function formatParamValueForInput(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object" && "value" in value && "unit" in value) {
    const v = value as { value: number; unit: string };
    return `${String(v.value)} ${v.unit}`;
  }
  return JSON.stringify(value);
}

// Sprint 80.Z B3 — local `formatMeasurement` helper removed. The
// inspector now calls `formatEngineering` from
// `@voltxampere/shared` (see import at the top of this file). The
// shared helper carries the same SI prefix + fraction-digit policy
// (BLUEPRINT §17.4 + ADR-0050) but adds locale-aware mantissa
// formatting via `Intl.NumberFormat`: Türkçe uses "," as the
// decimal separator while English uses "." — previously the
// inspector always rendered ASCII "." regardless of locale, a
// long-running Sprint 24 backlog item.

declare global {
  interface HTMLElementTagNameMap {
    "vxa-inspector": VxaInspector;
  }
}
